/**
 * The single place the app obtains a provider or signer for contract work.
 *
 * WHY THIS EXISTS
 *
 * `getActiveEip1193()` resolves the live EIP-1193 provider and ends with a
 * fallback to `window.ethereum`. That fallback is reachable more often than it
 * looks: `bridgeProvider` clears `eip6963Connector.provider` when a bridge fails
 * (providers/bridge.ts), and a connect that rejects never adopts one at all. The
 * app then quietly signs and calls through a DIFFERENT wallet, on whatever chain
 * that wallet happens to be sitting on — and nothing anywhere checked.
 *
 * That single gap has now produced three different bug reports:
 *
 *   - "unknown account #0" when signing, because `window.ethereum` had no
 *     authorized accounts on that origin (see hooks/wallet/useEagerConnect.ts).
 *   - `NotTraderOrOperator` reverts, because `msg.sender` was a different wallet
 *     than `trader` (see the note in utils/wallet/activeProvider.ts).
 *   - "Cannot read market lot size", because a `view` call landed on a chain
 *     where the DataStore address holds no contract.
 *
 * One cause, three faces. So the fix is here rather than at the ~15 call sites
 * that each built their own `Web3Provider`: those are the symptom surface, not
 * the defect.
 *
 * THE SPLIT
 *
 * Reads and writes have genuinely different requirements, and conflating them is
 * what made the third bug possible:
 *
 *   - A `view` call needs a node on the right chain. It does NOT need the user's
 *     wallet, and routing it there makes a read fail for reasons that have
 *     nothing to do with reading. Use `readProvider`.
 *   - A transaction needs the user's wallet AND must be on the expected chain,
 *     or it signs something for the wrong network. Use `getSignerForChain`,
 *     which refuses rather than guesses.
 *
 * `useSubaccounts` already reached for the configured RPC on its read path; this
 * makes that the rule instead of one file's good judgement.
 */
import { ethers } from "ethers";

import { ChainConfig } from "../../config/chains/types";
import {
  getActiveEip1193,
  getActiveEip1193ForRead,
} from "./activeProvider";

/**
 * The wallet is connected to a different chain than the market being traded.
 *
 * Carries both ids so the UI can name them and offer a switch, rather than
 * surfacing whatever the contract call happened to throw.
 */
export class WrongNetworkError extends Error {
  readonly expectedChainId: number;

  readonly actualChainId: number;

  readonly expectedChainName?: string;

  constructor(
    expectedChainId: number,
    actualChainId: number,
    expectedChainName?: string
  ) {
    // Name the target network, but never print "undefined" at a user: a config
    // without a display name still has an id, and the id alone is actionable.
    const target = expectedChainName
      ? `${expectedChainName} (${expectedChainId})`
      : `chain ${expectedChainId}`;
    super(
      `Wallet is connected to chain ${actualChainId}, but this market is on ` +
        `${target}. Switch networks in your wallet and try again.`
    );
    this.name = "WrongNetworkError";
    this.expectedChainId = expectedChainId;
    this.actualChainId = actualChainId;
    this.expectedChainName = expectedChainName;
  }
}

/** No usable EIP-1193 provider at all — nothing is connected. */
export class NoWalletProviderError extends Error {
  constructor() {
    super(
      "No wallet is connected. Connect a wallet and try again."
    );
    this.name = "NoWalletProviderError";
  }
}

/**
 * Read a chain id from whatever shape a provider returns.
 *
 * EIP-1193 specifies a hex string and every wallet in use returns one, but this
 * guard BLOCKS TRADING when it reads a mismatch, so a parsing slip is not a
 * cosmetic bug — it is a false refusal on a live order. `parseInt(raw, 16)`
 * alone turns a decimal "10143" into 65859, which would lock a user out of the
 * very chain they are correctly connected to. Only treat a string as hex when it
 * says it is.
 */
export function parseChainId(raw: unknown): number {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    return /^0x/i.test(s) ? parseInt(s, 16) : Number(s);
  }
  return Number.NaN;
}

export const isWrongNetworkError = (e: unknown): e is WrongNetworkError =>
  e instanceof WrongNetworkError ||
  (typeof e === "object" && e !== null && (e as Error).name === "WrongNetworkError");

/**
 * A provider for READ-ONLY calls: the chain's configured RPC.
 *
 * Deterministic by construction — it is on the right chain whatever the wallet
 * is doing, and it needs no wallet at all. A `view` call routed through the user's
 * wallet fails when they are on another network, which is how a lot-size lookup
 * came to block order placement.
 *
 * Falls back to the wallet only when a chain has no RPC configured, which would
 * be a config bug; better a degraded read than none.
 */
export function readProvider(config: ChainConfig): ethers.providers.Provider {
  const rpc = config.rpcUrls?.[0];
  if (rpc) {
    return new ethers.providers.JsonRpcProvider(rpc);
  }
  // The read-only variant: it does not refuse an unchosen wallet, because a read
  // has no `msg.sender` to get wrong. See getActiveEip1193ForRead.
  const eip1193 = getActiveEip1193ForRead();
  if (!eip1193) {
    throw new NoWalletProviderError();
  }
  return new ethers.providers.Web3Provider(eip1193);
}

/**
 * A signer for TRANSACTIONS, asserted to be on the expected chain.
 *
 * Throws `WrongNetworkError` rather than letting a signature be produced for the
 * wrong network — the failure a user can act on, instead of a contract revert
 * they cannot interpret.
 */
export async function getSignerForChain(
  config: ChainConfig
): Promise<ethers.Signer> {
  const provider = await getWeb3ProviderForChain(config);
  return provider.getSigner();
}

/**
 * The guarded `Web3Provider` itself, for the few callers that need the provider
 * rather than a signer (`callStatic` simulation, gas estimation).
 */
export async function getWeb3ProviderForChain(
  config: ChainConfig
): Promise<ethers.providers.Web3Provider> {
  const eip1193 = getActiveEip1193();
  if (!eip1193 || typeof eip1193.request !== "function") {
    throw new NoWalletProviderError();
  }

  // Ask the provider directly rather than trusting web3-react's cached chainId:
  // the cache is what goes stale when a provider is swapped underneath, which is
  // exactly the situation this guard exists for.
  const raw = await eip1193.request({ method: "eth_chainId" });
  const actual = parseChainId(raw);
  const expected = Number(config.chainId);

  if (!Number.isFinite(actual)) {
    throw new NoWalletProviderError();
  }
  if (actual !== expected) {
    throw new WrongNetworkError(expected, actual, config.name);
  }

  return new ethers.providers.Web3Provider(eip1193);
}
